import { styled } from '@linaria/react';
import { ActionIcon, Group, ScrollArea, Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useTaskFitTasks } from '../../../api/agent-miner-client/agent-miner-client.hooks';
import type { TaskFitTaskItem } from '../../../api/agent-miner-client/agent-miner-client.type';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import {
  inkDarkest,
  inkLight,
  inkLighter,
  redBase,
  secondaryBase,
  skyDark,
  skyLightest,
} from '../../../styles/design-tokens';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';
import { fmtMetric } from '../model-comparison/model-comparison.helpers';
import { resolveRowLabel } from './task-fit.helpers';
import { useTaskFitDimension, useTaskFitParams, useTaskFitSelectedCell } from './task-fit.hooks';

const dividerMargin = { margin: '16px 0' } as const;

/**
 * One task behind the selected matrix cell: the external code and name (linked
 * out when the task has a URL), the resolved task type, its cycle time, and a
 * rework marker when the task moved backward in the window.
 *
 * @param {{ task: TaskFitTaskItem }} props - Component props
 * @returns {JSX.Element} The task card
 */
const TaskListItem = ({ task }: { task: TaskFitTaskItem }) => {
  const { t } = useTranslation();
  const title = [task.code, task.name].filter(Boolean).join(' · ') || task.id;

  return (
    <ItemCard data-testid="task-fit-task-item">
      {task.url ? (
        <ItemTitleLink href={task.url} target="_blank" rel="noopener noreferrer">
          {title}
        </ItemTitleLink>
      ) : (
        <ItemTitle>{title}</ItemTitle>
      )}
      <MetaRow>
        <MetaItem>
          <Icon name="category" size={12} color={inkLighter} />
          {task.task_type}
        </MetaItem>
        {task.cycle_time_days !== null && (
          <MetaItem>
            <Icon name="timer" size={12} color={inkLighter} />
            {t('agent_miner.task_fit.tasks_panel_cycle', { days: fmtMetric(task.cycle_time_days) })}
          </MetaItem>
        )}
        {task.reworked && (
          <ReworkedBadge>
            <Icon name="undo" size={12} color={redBase} />
            {t('agent_miner.task_fit.tasks_panel_reworked')}
          </ReworkedBadge>
        )}
      </MetaRow>
    </ItemCard>
  );
};

/**
 * Side-section content for the Task Fit tab: the tasks behind the clicked
 * matrix cell, fetched from the task-fit tasks drill-down endpoint using the
 * same window and dimension as the matrix. Reworked tasks list first (the
 * backend's ordering); the footer notes when the list is capped below the
 * cell's full size.
 *
 * @returns {JSX.Element | null} The tasks side panel, or null when no cell is selected
 */
export const TaskFitTasksPanel = () => {
  const { t } = useTranslation();
  const params = useTaskFitParams();
  const { dimension } = useTaskFitDimension();
  const { cell, setCell } = useTaskFitSelectedCell();

  const { data, query } = useTaskFitTasks({
    organization_id: params.organization_id,
    start_date: params.start_date,
    end_date: params.end_date,
    dimension,
    row_key: cell?.rowKey ?? '',
    model_family: cell?.modelFamily ?? '',
  });

  if (!cell) return null;

  const tasks = data?.tasks ?? [];
  const totalTasks = data?.meta.total_tasks ?? 0;
  const rowLabel = resolveRowLabel({ row_key: cell.rowKey, label: cell.rowKey }, dimension);

  return (
    <Wrapper data-testid="task-fit-tasks-panel">
      <BrandedLoadingOverlay visible={query.isLoading} variant="colored" scoped />
      <Header>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text weight="bold" size="medium" color={secondaryBase}>
              {`${cell.modelFamily} (${totalTasks})`}
            </Text>
            <Text size="small" color={inkLighter}>
              {rowLabel}
            </Text>
          </Stack>
          <ActionIcon
            variant="subtle"
            size="sm"
            radius="xl"
            color={skyDark}
            onClick={() => setCell(null)}
            aria-label={t('common.actions.close')}
          >
            <Icon name="close" size={18} color={skyDark} />
          </ActionIcon>
        </Group>
      </Header>

      <Divider style={dividerMargin} />

      {query.isError ? (
        <EmptyState>
          <Icon name="error" size={32} color={inkLighter} />
          <Text size="small" color={inkLight}>
            {t('agent_miner.task_fit.tasks_panel_error')}
          </Text>
        </EmptyState>
      ) : !query.isLoading && tasks.length === 0 ? (
        <EmptyState>
          <Icon name="folder_open" size={32} color={inkLighter} />
          <Text size="small" color={inkLight}>
            {t('agent_miner.task_fit.tasks_panel_empty')}
          </Text>
        </EmptyState>
      ) : (
        <ScrollArea style={{ flex: 1, minHeight: 0 }} scrollbarSize={6}>
          <Stack gap={8} pb={8}>
            {tasks.map((task) => (
              <TaskListItem key={task.id} task={task} />
            ))}
            {totalTasks > tasks.length && (
              <Footnote>
                {t('agent_miner.task_fit.tasks_panel_showing', { shown: tasks.length, total: totalTasks })}
              </Footnote>
            )}
          </Stack>
        </ScrollArea>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  position: relative;
`;

const Header = styled.div`
  padding-top: 4px;
  flex-shrink: 0;
`;

const ItemCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  background: ${skyLightest};
  border-radius: 8px;
  padding: 10px;
`;

const ItemTitle = styled.span`
  font: 600 13px/1.3 'Figtree', sans-serif;
  color: ${inkDarkest};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const ItemTitleLink = styled.a`
  font: 600 13px/1.3 'Figtree', sans-serif;
  color: ${inkDarkest};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  text-decoration: none;

  &:hover {
    color: ${secondaryBase};
    text-decoration: underline;
  }
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font: 500 11.5px/1 'Figtree', sans-serif;
  color: ${inkLight};
  font-variant-numeric: tabular-nums;
`;

const ReworkedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font: 600 11.5px/1 'Figtree', sans-serif;
  color: ${redBase};
`;

const EmptyState = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  padding: 32px 12px;
`;

const Footnote = styled.div`
  font: 400 11px/1.4 'Figtree', sans-serif;
  color: ${inkLighter};
  text-align: center;
  padding: 4px 0;
`;
